<script>
export default {
  name: "SelectFulfillHouseModal",
};
</script>

<script setup>
import {
  computed,
  reactive,
  ref,
  toRefs,
  watch,
} from "vue";
import useVuelidate from "@vuelidate/core";
import { useToast } from "vue-toast-notification";
import { maxLength, helpers, requiredIf } from "@vuelidate/validators";
import { uuid } from "vue-uuid";
import Dialog from "primevue/dialog";
import Dropdown from "primevue/dropdown";
import { cloneDeep } from "lodash";
// import InputText from 'primevue/inputtext';
// import InputGroup from 'primevue/inputgroup';
// import Button from 'primevue/button';
// import InputGroupAddon from 'primevue/inputgroupaddon';
import InputNumber from 'primevue/inputnumber';
import BaseButton from "@/components/shared/Button/BaseButton.vue";
import BedFulfillmentModal from "@/components/StudentFulfillment/components/modals/BedFulfillmentModal";
import ButtonWithSpinner from "@/components/forms/SharedComponents/ButtonWithSpinner.vue";
import CloseIcon from "@/components/svg-icons/CloseIcon.vue";
import SimpleTextarea from "@/components/shared/SimpleTextarea.vue";
import Spinner from "@/components/helpers/Spinner.vue";
import { useGetHousing } from "@/components/StudentFulfillment/composables/housings";
import { useGetProfile } from "@/components/StudentFulfillment/composables/profiles";
import ordersService from "@/services/orders";
import orders from "@/services/orders";
import {
  checkValidRequestedRoom,
  getDisplayedRoom,
  usesRequestedRoom,
} from "@/components/StudentFulfillment/components/modals/HousingFulfillmentModal/utils";
import { hasDisplayStudentDiscountEnabled } from "@/composables/authorization";
import { centsToDollars, dollarsToCents } from "@/mixins/helpers";
import { convertCost } from "@/components/program-manager/sessions/utils";
import {
  CANCELLATION_REASONS_OTHER,
  HOUSING_CANCELLATION_REASONS,
  ERROR_TIMEOUT,
  HOUSING_TYPES_MAP,
  MAX_NOTE_TEXTAREA_LIMIT,
  ORDER_PRODUCT_STATUS,
  ORDER_STATUS,
  ORDER_PRODUCT_TYPE,
  REQUESTED_ROOM_TYPE,
  SUCCESS_TIMEOUT,
  TIERS_OBJ,
  LIBRARY_IDS,
  OPTIONS_PRICE_OVERRIDE,
  HOUSING_CONTEXT,
  ROOM_TYPES_OPTIONS,
} from "@/constants";
import {
  ERROR_MESSAGE,
  HOUSING_FULFILLMENT_MODAL_OPTIONS,
  OPTIONS_FULFILLMENT_STATUS,
} from "@/components/StudentFulfillment/components/modals/HousingFulfillmentModal/constants";
import { getOverrideReasons } from "@/composables/useOverrideReasons.js"
import {
  lineItemPriceHasChanged,
  setPriceOverrideDropdownOption,
  lineItemHasPendingOverride,
  generatePriceOverrideOptions,
  generateDefaultPriceOverride,
  buildStatusRow,
  canCreatePriceOverride,
  canUpdatePriceOverride,
  findLatestPrice,
  showCurrentPrice
} from "@/components/StudentFulfillment/components/modals/utils.ts";
import { useCreatePriceOverride, getQueryKey as createKey } from "@/providers/priceOverrides/usePriceOverrideCreate";
import { usePatchPriceOverride, getQueryKey as patchKey } from "@/providers/priceOverrides/usePriceOverridePatch";
import { usePriceOverridesRevert } from "@/providers/priceOverrides/usePriceOverridesRevert";
import { useGetSessionPricing } from "@/providers/sessions/useGetSessionPricing";
import { useQueryClient } from '@tanstack/vue-query'
import { datadogLogs } from "@datadog/browser-logs";

const { keepPriceReasons, customPriceReasons } = getOverrideReasons();
const toast = useToast();
const queryClient = useQueryClient();

const props = defineProps({
  modalIsOpen: {
    type: Boolean,
    default: true,
  },
  order: {
    type: Object,
    required: true,
  },
  sessionPrices: {
    type: Object,
    default: () => {}
  },
  programSession: {
    type: Object,
    default: () => {},
  },
});

const { mutateAsync: createPriceOverrideRequest } = useCreatePriceOverride({
  onSuccess: async (data, variables) => {
    await queryClient.invalidateQueries({ queryKey: createKey() });
    toast.open({
      message: `Created Price Override for ${variables.name}`,
      type: "success",
      position: "bottom",
      duration: SUCCESS_TIMEOUT,
    });
  },
  onError: (error, variables) => {
    datadogLogs.logger.warn("Failed to create price override.", {
      error: error.message,
    });
    toast.open({
      message: `Couldn't create price override for ${variables.name}`,
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
  },
});
const { mutateAsync: patchPriceOverrideRequest } = usePatchPriceOverride({
  onSuccess: async ({ id }, variables) => {
    await queryClient.invalidateQueries({ queryKey: patchKey(id) });
    toast.open({
      message: `Updated Price Override for ${variables.name}`,
      type: "success",
      position: "bottom",
      duration: SUCCESS_TIMEOUT,
    });
  },
  onError: (error, variables) => {
    datadogLogs.logger.warn("Failed to update price override.", {
      error: error.message,
    });
    toast.open({
      message: `Couldn't update price override for ${variables.name}`,
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
  },
});

const { mutateAsync: revertPriceOverrideRequest } = usePriceOverridesRevert({
  onSuccess: async (data, variables) => {
    if (Array.isArray(data?.errors) && data.errors.length > 0) {
      datadogLogs.logger.warn("Failed to revert price override for ids.", {
        errors: data?.errors,
      });
      toast.open({
        message: `Couldn't Revert price override for ${variables.name}`,
        type: "error",
        position: "bottom",
        duration: ERROR_TIMEOUT,
      });
    } else {
      toast.open({
        message: `Reverted Price Override for ${variables.name}`,
        type: "success",
        position: "bottom",
        duration: SUCCESS_TIMEOUT,
      });
    }
  },
  onError: (error, variables) => {
    datadogLogs.logger.warn("Failed calling revert price override.", {
      error: error.message,
      priceOverrideId: variables?.payload ?? "unknown",
    });
    toast.open({
      message: `Couldn't Revert price override for ${variables.name}`,
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
  },
});


const createPriceOverride = async (line_item) => {
  const createResponse = await createPriceOverrideRequest({
    payload: {
      request_notes: line_item.price_override.request_notes,
      override_amount_in_cents: dollarsToCents(line_item.price_override.override_amount_in_dollars),
      order_id: line_item.price_override.order_id,
      product_id: line_item.price_override.product_id,
      override_reason_id: line_item.price_override.override_reason_id,
      library_id: line_item.price_override.library_id,
    },
    name: line_item?.name,
  });
  return createResponse;
};

const patchPriceOverride = async (line_item) => {
  const updateResponse = await patchPriceOverrideRequest({
    id: line_item.price_override.id,
    payload: {
      override_amount_in_cents: dollarsToCents(line_item.price_override.override_amount_in_dollars),
      override_reason_id: line_item.price_override.override_reason_id,
      request_notes: line_item.price_override.request_notes,
    },
    name: line_item?.name,
  });
  return updateResponse;
};

const revertPriceOverride = async (line_item) => {
  const revertResponse = await revertPriceOverrideRequest({
    payload: {
      decision_notes: "User reverted to original price.",
      id: line_item.price_override.id
    },
    name: line_item?.name,
  });
  return revertResponse;
};

const emit = defineEmits(["closeModal"]);

const { modalIsOpen, order, sessionPrices, programSession } = toRefs(props);

const optionSelectedId = ref(0);

const isEditingRequestedRoom = ref(false);

// Context: used in dropdown to change requested room, it's not the actual requested room after fulfilling
const requestedRoom = ref({});

const state = reactive({
  isSavingOrder: false,
  isSavingOrderRoom: false,
});

const modalStates = reactive({
  hideModal: false,
  openSelectBedsModal: false,
});

const housingFulfillmentOrderData = reactive({
  roomIdToFetch: undefined, // requested room OR new room to assign
  roomRequested: {}, // room after fulfilling
  roomAssignmentSelected: [], // NEW room to be assigned
  orderHousingData: {}, // current housing from order
  cancellationReason: {
    option: undefined,
    text: "",
  },
  price_override: {},
});


const sessionPricingPayload = computed(() => {
  // Note on Additional Fees and Add-ons:
  // No need to separate them here, backend filters them out correspondingly
  return {
    session_id: order.value?.order?.session_id,
    home_institution_id: order.value?.order?.home_institution_id,
    rooms: housingFulfillmentOrderData.roomIdToFetch ? [housingFulfillmentOrderData.roomIdToFetch] : [],
    internships: order.value?.order?.internship_ids ?? [],
    event_occurrences: order.value?.order?.event_occurrence_ids ?? [],
    addons: order.value?.order?.misc_product_ids ?? [],
    additional_fees: order.value?.order?.misc_product_ids ?? [],
    ...(hasDisplayStudentDiscountEnabled.value
      ? { special_pricing_override_entity_id: order.value?.order?.special_pricing_override_entity_id }
      : {})
  }
});

// This is mainly to get the pricing from
const {
  isFetching: sessionPricingFetching,
  data: newBedSessionPricingData,
  refetch: executeFetchPricing
} = useGetSessionPricing(
  {
    request: sessionPricingPayload,
    useQueryOptions:
      {
        staleTime: 0,
        enabled: false
      },
    get_active: ref(true)
  }
);

const session = computed(() => {
  const metadata = order?.value?.payment_processing?.order_metadata;
  return {
    session_id: metadata?.session_id,
    city_id: metadata?.session_city_id,
    city: metadata?.session_city,
    session_types: metadata?.program_type?.split("; "),
    arrival_date: metadata?.start_date,
    departure_date: metadata?.end_date,
  };
});

const orderRoomTypes = computed(() => {
  return order?.value?.product_details?.rooms?.map(({ room }) => ({
    id: room?.type,
    label: room?.type,
  }));
});

const housingId = computed(() => {
  const room = order?.value?.product_details?.rooms?.[0];
  return room?.room?.unit?.housing?.id ?? room?.bed?.housing_id;
});

const roomTypeOptions = computed(
  () =>
    programSession.value?.items?.session_units?.flatMap(
      ({ unit }) =>
        unit?.rooms
          ?.filter((room) =>
            checkValidRequestedRoom(
              room,
              order?.value?.payment_processing?.order_metadata?.start_date
            )
          )
          ?.map((room) => ({ ...room, housing: { ...unit?.housings } })) || []
    ) || []
);

const orderRoomIsEditable = computed(() =>
  [
    ORDER_STATUS.draft,
    ORDER_STATUS.submitted,
    ORDER_STATUS.paidConfirmed,
  ].includes(order.value?.order?.status)
);


const toBeassignedRoomType = computed(() => REQUESTED_ROOM_TYPE?.[housingFulfillmentOrderData?.roomAssignmentSelected?.[0]?.room_type]);

const assignedRoomType = computed(() => REQUESTED_ROOM_TYPE?.[housingFulfillmentOrderData?.orderHousingData?.room_type]);

const participant = computed(() => ({
  id: order?.value?.order?.participant_id,
  name: order?.value?.order?.participant_first_name ?? "",
  fullname: order?.value?.order?.participant_fullname ?? "",
}));

const needs_advising = computed(
  () => profileOptions.value?.needs_advising ?? false
);

const isHousingLocalHost = computed(
  () => housingFulfillmentOrderData.orderHousingData?.type === HOUSING_TYPES_MAP.LOCAL_HOST
);

const isLoading = computed(
  () =>
    !order?.value?.order?.id ||
    (!!housingLoading?.value && !housingIsReady?.value) ||
    (!!profileLoading?.value && !profileIsReady?.value) ||
    (!!sessionPricingFetching?.value && sessionPricingFetching.value)
);

const showCancellationReasonText = computed(
  () =>
    optionSelectedId.value === HOUSING_FULFILLMENT_MODAL_OPTIONS.canceled &&
    housingFulfillmentOrderData?.cancellationReason?.option?.id ==
    CANCELLATION_REASONS_OTHER
);

const showRoomAssignment = computed(
  () =>
    optionSelectedId.value === HOUSING_FULFILLMENT_MODAL_OPTIONS.fulfillment &&
    !needs_advising.value &&
    housingFulfillmentOrderData?.roomAssignmentSelected?.length
);

const showCurrentRoomAssignment = computed(
  () =>
    optionSelectedId.value === HOUSING_FULFILLMENT_MODAL_OPTIONS.fulfillment &&
    !!order?.value?.product_details?.rooms?.[0]?.bed?.id &&
    !!order?.value?.product_details?.rooms?.[0]?.room?.unit?.id
);

const canEditRequestedRoomType = computed(() => {
  const housingFulfillmentStatus =
    order.value?.product_details?.rooms?.[0]?.status;
  return (
    housingFulfillmentStatus !== ORDER_PRODUCT_STATUS.fulfilled &&
    housingFulfillmentStatus !== ORDER_PRODUCT_STATUS.canceled &&
    optionSelectedId.value !== HOUSING_FULFILLMENT_MODAL_OPTIONS.fulfillment &&
    optionSelectedId.value !== HOUSING_FULFILLMENT_MODAL_OPTIONS.canceled
  );
});

const updateRoom = async () => {
  state.isSavingOrderRoom = true;
  try {
    const oldProductId = order.value?.product_details?.rooms?.[0]?.room?.id;
    let response = null;

    if (oldProductId) {
      response = await orders.updateOrderLineItem({
        orderId: order.value.order.id,
        productId: oldProductId,
        payload: {
          new_product_id: requestedRoom.value.id,
          status: order.value?.product_details?.rooms?.[0]?.status,
          quantity: order.value?.product_details?.rooms?.[0]?.quantity,
        },
        skipErrorBus: true,
      });

      order.value.product_details = response?.data?.data?.product_details;
      await setRoomHousing();
      isEditingRequestedRoom.value = false;
      toast.open({
        message: "Order room updated successfully",
        type: "success",
        position: "bottom",
        duration: SUCCESS_TIMEOUT,
      });
    } else {
      response = await orders.createOrderLineItem({
        orderId: order.value.order.id,
        payload: {
          new_product_id: requestedRoom.value.id,
          status: order.value?.product_details?.rooms?.[0]?.status,
          product_type: ORDER_PRODUCT_TYPE.room,
        },
        skipErrorBus: true,
      });
      handleCloseModal(true);
    }
  } catch (exception) {
    toast.open({
      message: `${ERROR_MESSAGE}`,
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
  } finally {
    state.isSavingOrderRoom = false;
  }
};

const getRoomType = (roomType) => {
  return !roomType ? "" : roomType === ROOM_TYPES_OPTIONS[0]["label"]
    ? REQUESTED_ROOM_TYPE["1 bed"]
    : REQUESTED_ROOM_TYPE["2 beds"];
};

const hasCityAndCountry = (housing) => {
  return housing?.city_id_rel && housing?.city_id_rel.country;
};

const getCityAndCountry = (housing) => {
  if (hasCityAndCountry(housing)) {
    return `${housing.city_id_rel.city}, ${housing.city_id_rel.admin_name}, ${housing.city_id_rel.country.name}`;
  } else {
    return "";
  }
};

// Current housing
const {
  isLoading: housingLoading,
  isReady: housingIsReady,
  execute: executeFetchHousing,
  state: housingResponse,
  error: housingError,
} = useGetHousing({ immediate: false});

// Student Profile
const {
  isLoading: profileLoading,
  isReady: profileIsReady,
  execute: executeFetchProfile,
  state: profileOptions,
} = useGetProfile({ immediate: false, throwError: true });

const openBedsModal = () => {
    modalStates.openSelectBedsModal = true;
    modalStates.hideModal = true;
};

const handleRadioOption = (optionSelected) => {
  if (!state.isSavingOrder) {
    if (!needs_advising.value) {
      optionSelectedId.value = optionSelected;
      if (
        optionSelected === HOUSING_FULFILLMENT_MODAL_OPTIONS.fulfillment &&
        !isHousingLocalHost.value
      ) {
        housingFulfillmentOrderData.roomAssignmentSelected = [];
      }
    } else if (
      optionSelected !== HOUSING_FULFILLMENT_MODAL_OPTIONS.fulfillment &&
      optionSelected !== HOUSING_FULFILLMENT_MODAL_OPTIONS.canceled
    ) {
      optionSelectedId.value = optionSelected;
    }
  }
};

const displayBedSearchRow = computed(() => {
  return optionSelectedId.value === HOUSING_FULFILLMENT_MODAL_OPTIONS.fulfillment && !isHousingLocalHost.value;
});

const userSelectedLatestPrice = computed(() => {
  return housingFulfillmentOrderData.price_override?.price_option === OPTIONS_PRICE_OVERRIDE.KEEP_CURRENT.value;
});

const handleCloseModal = (refreshOrder = false) => {
  state.isSavingOrder = false;
  optionSelectedId.value = HOUSING_FULFILLMENT_MODAL_OPTIONS.empty;
  housingFulfillmentOrderData.roomAssignmentSelected = [];
  housingFulfillmentOrderData.cancellationReason.option = undefined;
  housingFulfillmentOrderData.cancellationReason.text = "";
  isEditingRequestedRoom.value = false;
  if (refreshOrder) {
    queryClient.invalidateQueries({queryKey: ["order"]});
  }
  emit("closeModal");
};

const handleCloseBedsModal = () => {
  modalStates.openSelectBedsModal = false;
  modalStates.hideModal = false;
  if (!housingFulfillmentOrderData?.roomAssignmentSelected?.length) {
    optionSelectedId.value = HOUSING_FULFILLMENT_MODAL_OPTIONS.empty;
  }
};

const updateSelectedItems = async (items) => {
  housingFulfillmentOrderData.roomAssignmentSelected =
    items.map((item) => ({
      ...item,
      bed_id: item?.id,
      room_name: `${item?.room_type} | ${item?.id}`,
    })) || [];

    housingFulfillmentOrderData.roomAssignmentSelected[0].location = setHousingLocation(housingFulfillmentOrderData.roomAssignmentSelected?.[0]);
    // Get latest pricing with new selected bed
    housingFulfillmentOrderData.roomIdToFetch = housingFulfillmentOrderData.roomAssignmentSelected[0].room_id;
    await executeFetchPricing();

    // Add pricing to assigned room
    const newRoomPrice = newBedSessionPricingData.value?.rooms?.find((room) => room?.product_id === housingFulfillmentOrderData.roomAssignmentSelected?.[0]?.room_id);
    if (newRoomPrice) {
      housingFulfillmentOrderData.roomAssignmentSelected[0].total_price_in_cents = newRoomPrice.total_price_in_cents;
      housingFulfillmentOrderData.roomAssignmentSelected[0].previous_bed_price_in_cents = order.value.product_details.rooms[0].total_price_in_cents;
      housingFulfillmentOrderData.roomAssignmentSelected[0].latest_unapproved_price_override = order.value.product_details.rooms[0]?.latest_unapproved_price_override;
      housingFulfillmentOrderData.roomAssignmentSelected[0].latest_approved_price_override = order.value.product_details.rooms[0]?.price_override;
    } else {
      toast.open({
        message: "We could not find a price for the room when one was needed.",
        type: "error",
        position: "bottom",
        duration: ERROR_TIMEOUT,
      });
      handleCloseModal();
    }
    // Rebuild price options
    housingFulfillmentOrderData.price_override = buildPriceOverride(orderPricingOrNewRoom.value.lineItem);
};

const productId = computed(
  () => order?.value?.product_details?.rooms?.[0]?.room?.id
);

const createOrUpdatePriceOverride = async () => {
  let noOperationMade = true;

  if (canCreatePriceOverride(true, housingFulfillmentOrderData.price_override)) {
    await createPriceOverride(housingFulfillmentOrderData.price_override);
    noOperationMade = false;
    handleCloseModal(true);
  } else if (canUpdatePriceOverride(true, housingFulfillmentOrderData.price_override)) {
    await patchPriceOverride(housingFulfillmentOrderData.price_override);
    handleCloseModal(true);
    noOperationMade = false;
  }

  // Don't notify user if he didn't attempt to use overrides
  if (noOperationMade && !userSelectedLatestPrice.value) {
    toast.open({
      message: "We didn’t find any changes to apply. If you meant to create or update a price override, please fill out the required fields.",
      type: "warning",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
  }
  state.isSavingOrder = false;
};

const cancelOrRevert = async (line_item) => {
  // MVP: Just revert, cancel will come later
  // if (lineItemHasPendingOverride(line_item)) {
  // ....
  //}
  await revertPriceOverride(line_item);
  handleCloseModal(true);
};

const executeSaveAndClose = async ({ payload }) => {
  try {
    v$.value.$reset();
    const isValid = await v$.value.$validate();
    const orderId = order?.value?.order?.id;

    if (isValid && orderId) {
      let response = null;

      if (productId.value) {
        response = await ordersService.updateOrderLineItem({
          orderId: orderId,
          productId: productId.value,
          payload: {
            ...payload,
            use_latest_product_price: findLatestPrice(housingFulfillmentOrderData.price_override),
          },
          skipErrorBus: true,
        });
      } else {
        response = await ordersService.createOrderLineItem({
          orderId: orderId,
          payload: {
            status: ORDER_PRODUCT_STATUS.unfulfilled,
            product_type: ORDER_PRODUCT_TYPE.room,
            new_product_id: requestedRoom.value.id,
            use_latest_product_price: findLatestPrice(housingFulfillmentOrderData.price_override)
          },
          skipErrorBus: true,
        });
      }
      const productDetails = response?.data?.data?.product_details;
      if (productDetails) {
        toast.open({
          message: "Order updated Successfully",
          type: "success",
          position: "bottom",
          duration: SUCCESS_TIMEOUT,
        });
        order.value.product_details = productDetails;

        await createOrUpdatePriceOverride();

        handleCloseModal(userSelectedLatestPrice.value);
      }
    } else {
      toast.open({
        message: `Unable to save Housing, make sure all the required fields are completed.`,
        type: "error",
        position: "bottom",
        duration: ERROR_TIMEOUT,
      });
    }
  } catch (err) {
    toast.open({
      message: `${ERROR_MESSAGE}`,
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
  } finally {
    state.isSavingOrder = false;
  }
};

const saveHousingFulfillmentOrder = async () => {
  state.isSavingOrder = true;
  const isFulfillingHousing = optionSelectedId.value === HOUSING_FULFILLMENT_MODAL_OPTIONS.fulfillment;
  const bedAssigned = Boolean(order.value.product_details?.rooms?.[0]?.bed?.id) || Boolean (housingFulfillmentOrderData.roomAssignmentSelected[0]?.bed_id)

  // Validation
  if (isFulfillingHousing && !isHousingLocalHost.value && !bedAssigned) {
      toast.open({
        message: "Please assign a bed before fulfilling",
        type: "error",
        position: "bottom",
        duration: ERROR_TIMEOUT,
      });
    state.isSavingOrder = false;
    return;
  }

 if (isFulfillingHousing && !isHousingLocalHost.value) {
      if (housingFulfillmentOrderData.roomAssignmentSelected[0]?.id) {
        // User selecting specific bed
        await executeSaveAndClose({
          payload: {
            new_product_id: housingFulfillmentOrderData.roomAssignmentSelected[0].room_id,
            bed_id: housingFulfillmentOrderData.roomAssignmentSelected[0].bed_id,
            status: ORDER_PRODUCT_STATUS.fulfilled,
          },
        });
      } else if (userSelectedLatestPrice.value && !Boolean(housingFulfillmentOrderData.roomAssignmentSelected[0]?.id)) {
        // User didn't specify new bed but wants to use latest price
        await executeSaveAndClose({
          payload: {
            status: ORDER_PRODUCT_STATUS.fulfilled,
          },
        });
      } else if (!Boolean(housingFulfillmentOrderData.roomAssignmentSelected[0]?.id)) {
        // Attempt to create/update price override if needed
        await createOrUpdatePriceOverride();
      }
  } else if (isFulfillingHousing) {
    await executeSaveAndClose({
      payload: {
        status: ORDER_PRODUCT_STATUS.fulfilled,
      },
    });
  } else {
    // Ready for Fulfillment | Canceled
    const payload = {
      bed_id: order?.value?.product_details?.rooms?.[0]?.bed?.id,
      status: ORDER_PRODUCT_STATUS.unfulfilled,
      cancellation_reason: undefined,
    };

    if (optionSelectedId.value === HOUSING_FULFILLMENT_MODAL_OPTIONS.canceled) {
      payload.status = ORDER_PRODUCT_STATUS.canceled;
      payload.cancellation_reason =
        housingFulfillmentOrderData?.cancellationReason?.option?.label;
      if (showCancellationReasonText.value) {
        payload.cancellation_reason =
          housingFulfillmentOrderData?.cancellationReason?.text;
      }
    }
    await executeSaveAndClose({ payload });
  }
};

const durations = computed(() => {
  const requestedRoomDays = programSession.value?.items?.session_travel_details?.[0]?.duration ?? 0;
  const requestedRoomLabel = requestedRoomDays === 1 ? "Day" : requestedRoomDays >= 2 ? "Days" : "";
  const assignedRoomDays = order?.value?.order?.travel_duration ?? 0;
  const assignedRoomLabel = assignedRoomDays === 1 ? "Day" : assignedRoomDays >= 2 ? "Days" : "";
  return {
    requestedRoomDays,
    requestedRoomLabel,
    assignedRoomDays,
    assignedRoomLabel,
  };
});

const orderPricingOrNewRoom = computed(() => {
  // If user is assigning new bed, we use the pricing obtained for the new bed
  if (optionSelectedId.value === HOUSING_FULFILLMENT_MODAL_OPTIONS.fulfillment && Boolean(housingFulfillmentOrderData?.roomAssignmentSelected?.length) && Boolean(newBedSessionPricingData.value?.rooms?.length)) {
    return {
      label: "newRoom",
      prices: newBedSessionPricingData.value?.rooms,
      lineItem: housingFulfillmentOrderData.roomAssignmentSelected[0]
    }
  }

  // Default - We're updating the existing bed in room, use the pricing previously fetched
  return {
    label: "order",
    prices: sessionPrices.value?.rooms,
    lineItem: order.value?.product_details?.rooms?.[0]
  }
});

const buildPriceOverride = (line_item) => {
  // Ways the object is built:
  // 1 - When modal initializes
  // 2 - When new bed is assigned
  // 3 - When changing statuses (ex: new bed was going to be assigned but they moved the status back to canceled w/o fulfilling)
  line_item.latest_approved_price_override = line_item?.price_override ?? line_item?.latest_approved_price_override;
  const current_session_pricing = lineItemPriceHasChanged(line_item, orderPricingOrNewRoom.value.prices, LIBRARY_IDS.housing);
  const price_dropdown_default_option = setPriceOverrideDropdownOption(line_item, current_session_pricing);
  const pending_price_override = lineItemHasPendingOverride(line_item) ? line_item.latest_unapproved_price_override : {};

  return {
    current_session_pricing: current_session_pricing,
    current_price_config: showCurrentPrice(pending_price_override?.id, current_session_pricing?.total_price_in_cents, line_item?.total_price_in_cents),
    name: line_item?.room?.unit?.housing?.name ?? line_item?.name,
    pending_price_override: Boolean(pending_price_override?.id),
    total_price: line_item?.total_price_in_cents ? `${convertCost(centsToDollars(line_item?.total_price_in_cents))}` : line_item?.product_price,
    price_options: generatePriceOverrideOptions(line_item, current_session_pricing, price_dropdown_default_option),
    price_option: price_dropdown_default_option,
    price_override: generateDefaultPriceOverride(line_item, price_dropdown_default_option, pending_price_override, LIBRARY_IDS.housing, order.value.order.id),
    price_override_status: buildStatusRow(line_item),
    disable_price_dropdown: Boolean(pending_price_override?.id),
    latest_approved_price_override: line_item.latest_approved_price_override,
  };
};

const setHousingStatus = (currentStatus = "") => {
  switch (currentStatus) {
    case ORDER_PRODUCT_STATUS.fulfilled:
      optionSelectedId.value = HOUSING_FULFILLMENT_MODAL_OPTIONS.fulfillment;
      break;
    case ORDER_PRODUCT_STATUS.unfulfilled:
      if (!needs_advising.value) {
        optionSelectedId.value = HOUSING_FULFILLMENT_MODAL_OPTIONS.ready_fulfillment;
      }
      break;
    case ORDER_PRODUCT_STATUS.canceled:
      optionSelectedId.value = HOUSING_FULFILLMENT_MODAL_OPTIONS.canceled;

      const reason = order.value?.product_details?.rooms?.[0]?.cancellation_reason;
      housingFulfillmentOrderData.cancellationReason.option = {
        id: reason,
        label: reason,
      };
      housingFulfillmentOrderData.cancellationReason.text = reason;
      break;
    default:
      optionSelectedId.value = HOUSING_FULFILLMENT_MODAL_OPTIONS.empty;
  }
}

const ensureRequestedRoomPrice = async () => {
  // Get Price of requested room
  housingFulfillmentOrderData.roomIdToFetch = housingFulfillmentOrderData.roomRequested.id;
  await executeFetchPricing();
  const requestedRoomPrice = newBedSessionPricingData.value?.rooms?.find((room) => room?.product_id === housingFulfillmentOrderData.roomRequested.id);

  if (requestedRoomPrice) {
    housingFulfillmentOrderData.roomRequested.total_price_in_cents = !housingFulfillmentOrderData.roomRequested.total_price_in_cents ? requestedRoomPrice.total_price_in_cents : housingFulfillmentOrderData.roomRequested.total_price_in_cents;
    housingFulfillmentOrderData.roomRequested.price_per_day = requestedRoomPrice.product_price_per_day;
  } else {
    toast.open({
      message: "We could not find a price for the requested room.",
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
  }
};

const setHousingLocation = (housingData) => {
  const city = housingData?.city;
  const country = housingData?.country?.name || housingData?.country;

  if (city && !country) {
    return city;
  } else if (!city && country) {
    return country;
  } else if (city && country) {
    return `${city}, ${country}`;
  }
  return "";
};

const setRequestedHousing = async (currentHousing) => {
  const requestedRoomHousingId = order.value?.product_details?.rooms?.[0]?.requested_room?.unit?.housing?.id;
  if (requestedRoomHousingId && currentHousing?.id) {
    // Already assigned to a room, requested room was the previous room
    housingFulfillmentOrderData.roomRequested = order.value.product_details.rooms[0].requested_room;
    housingFulfillmentOrderData.roomRequested.total_price_in_cents = order.value.product_details.rooms[0].price_before_fulfillment_in_cents;
    housingFulfillmentOrderData.roomRequested.context = HOUSING_CONTEXT.requested;

    const fetchRequestedHousing = requestedRoomHousingId !== currentHousing.id;
    if (fetchRequestedHousing) {
      await executeFetchHousing(0, requestedRoomHousingId); // Other housing
    }

    const requestedHousing = fetchRequestedHousing ? housingResponse.value : currentHousing;
    housingFulfillmentOrderData.roomRequested.housing_data = cloneDeep(requestedHousing);
    housingFulfillmentOrderData.roomRequested.location = setHousingLocation(housingFulfillmentOrderData.roomRequested.housing_data);

    await ensureRequestedRoomPrice();
  }
};


const setRoomHousing = async () => {
  if (housingId.value) {
    const orderRoom = order.value?.product_details?.rooms?.[0];

    // Current order room
    await executeFetchHousing(0, housingId.value);
    housingFulfillmentOrderData.orderHousingData = cloneDeep(housingResponse.value);
    housingFulfillmentOrderData.orderHousingData.room_type = orderRoom?.room?.type;
    housingFulfillmentOrderData.orderHousingData.location = setHousingLocation(housingFulfillmentOrderData.orderHousingData);
    housingFulfillmentOrderData.orderHousingData.price_per_day = orderRoom?.price_per_day;
    housingFulfillmentOrderData.orderHousingData.bed_name = orderRoom?.bed?.name;

    if (usesRequestedRoom(orderRoom)) {
      await setRequestedHousing(housingFulfillmentOrderData.orderHousingData);
    } else {
      // Current room is the requested room (for now)
      housingFulfillmentOrderData.roomRequested.housing_data = cloneDeep(housingFulfillmentOrderData.orderHousingData);
      housingFulfillmentOrderData.roomRequested.location = setHousingLocation(housingFulfillmentOrderData.roomRequested.housing_data);
      housingFulfillmentOrderData.roomRequested.total_price_in_cents = orderRoom?.total_price_in_cents;
      housingFulfillmentOrderData.roomRequested.price_per_day = orderRoom?.price_per_day;
      housingFulfillmentOrderData.roomRequested["context"] = HOUSING_CONTEXT.generic;
      housingFulfillmentOrderData.roomRequested["type"] = orderRoom?.id;
      housingFulfillmentOrderData.roomRequested["id"] = orderRoom?.room?.id;
    }

    // Dropdown - seeded from whichever room the column above is showing, so Save never
    // sends a room the user was not looking at
    const displayedRoomId = getDisplayedRoom(orderRoom)?.id;
    requestedRoom.value = roomTypeOptions.value?.find((room) => room?.id === displayedRoomId);
  }
};

watch(
  () => order.value.order.id,
  async (open) => {
    setHousingStatus(order.value?.product_details?.rooms?.[0]?.status);

    if (open && participant.value?.id) {
      await executeFetchProfile(0, participant.value?.id);
      await setRoomHousing();
      const defaultPriceOVerride = housingId.value ? buildPriceOverride(order.value.product_details.rooms[0]) : {};
      housingFulfillmentOrderData.price_override = defaultPriceOVerride;
    } else {
      housingResponse.value = undefined;
      profileOptions.value = undefined;
    }
  },
  { immediate: true }
);

watch(
  () => housingError.value,
  (error) => {
    toast.open({
      message: `Failed to load housing. (${error})`,
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
  }
);


const mustHaveReason = (value, vm) => {
  const isFulfilledOrCancel =
    [HOUSING_FULFILLMENT_MODAL_OPTIONS.fulfillment,
      HOUSING_FULFILLMENT_MODAL_OPTIONS.canceled]
      .includes(optionSelectedId.value);

  const isUsingPriceOverride =
    [OPTIONS_PRICE_OVERRIDE.KEEP_ORDER.value,
      OPTIONS_PRICE_OVERRIDE.CUSTOM_PRICE.value]
      .includes(vm?.price_option)

  if (isFulfilledOrCancel && isUsingPriceOverride) {
    return Boolean(value?.override_reason_id);
  }

  return true;
};

const rules = computed(() => ({
  cancellationReason: {
    option: {
      required: helpers.withMessage(
        "This field is required",
        requiredIf(
          () =>
            optionSelectedId.value ===
            HOUSING_FULFILLMENT_MODAL_OPTIONS.canceled
        )
      ),
    },
    text: {
      required: helpers.withMessage(
        "This field is required",
        requiredIf(
          () =>
            optionSelectedId.value ===
              HOUSING_FULFILLMENT_MODAL_OPTIONS.canceled &&
            showCancellationReasonText.value
        )
      ),
      maxLength: maxLength(MAX_NOTE_TEXTAREA_LIMIT),
    },
  },
  price_override: {
    price_override: {
      override_reason_id: mustHaveReason,
    },
  }
}));

const requestedRoomType = computed(() => {
  // The dropdown only holds a room that survived the roomTypeOptions filter, so fall back
  // to the order payload for a room that is no longer offered
  const fallbackRoomType = getDisplayedRoom(order.value?.product_details?.rooms?.[0])?.type;

  return getRoomType(requestedRoom.value?.type ?? fallbackRoomType);
});

const v$ = useVuelidate(rules, housingFulfillmentOrderData, {
  $registerAs: `FulfillmentHousingModal-${uuid.v4()}`,
  $lazy: true,
  $autoDirty: true,
});

const modalHeaderPassthrough = {
  root: {
    class: "w-3/5",
  },
  header: {
    class: ["bg-emerald-50", "min-h-65", "flex", "justify-between", "items-center", "pl-5", "pr-2", "rounded-t-lg", "border", "border-white"],
  },
}

const fulfillmentOptions = computed(() => {
  return OPTIONS_FULFILLMENT_STATUS.filter((option) => ![0, 4].includes(option.value))
});

const updatePriceOverride = (line_item) => {
  // Triggers how the price override should be presented
  housingFulfillmentOrderData.price_override.price_override = generateDefaultPriceOverride(line_item, housingFulfillmentOrderData.price_override.price_option, lineItemHasPendingOverride(line_item) ? line_item.latest_unapproved_price_override : {}, LIBRARY_IDS.housing, order.value.order.id);
};

const rebuildPriceOverride = () => {
  // Rebuilds entire price dropdown
  housingFulfillmentOrderData.price_override = buildPriceOverride(orderPricingOrNewRoom.value.lineItem);
};

const fulfillingLocalHost = computed(() => {
  return optionSelectedId.value === HOUSING_FULFILLMENT_MODAL_OPTIONS.fulfillment && isHousingLocalHost.value;
});

const total_price = computed(() => {
  // Formatted price in cents for different items
  const to_be_assigned_price_per_day = Number(housingFulfillmentOrderData.roomAssignmentSelected?.[0]?.price_per_day);
  const actual_room_assigned_price_per_day = Number(housingFulfillmentOrderData?.orderHousingData?.price_per_day);
  const requested_price_per_day = Number(housingFulfillmentOrderData.roomRequested.price_per_day);
  const total = Number(housingFulfillmentOrderData.roomRequested.total_price_in_cents);

  return {
    to_be_assigned_price_per_day: !Number.isNaN(to_be_assigned_price_per_day) ? convertCost(centsToDollars(housingFulfillmentOrderData.roomAssignmentSelected[0].price_per_day)) : "",
    actual_room_assigned_price_per_day: !Number.isNaN(actual_room_assigned_price_per_day) ? convertCost(centsToDollars(housingFulfillmentOrderData.orderHousingData.price_per_day)) : "",
    requested_room: !Number.isNaN(total) ? convertCost(centsToDollars(housingFulfillmentOrderData.roomRequested.total_price_in_cents)) : "",
    requested_price_per_day: !Number.isNaN(requested_price_per_day) ? convertCost(centsToDollars(housingFulfillmentOrderData.roomRequested.price_per_day)) : "",
  }

});

const changeHousingNameColor = computed(() => {
  const requestedHousingName = housingFulfillmentOrderData.roomRequested?.housing_data?.name;
  if (showCurrentRoomAssignment.value) {
    return requestedHousingName !== housingFulfillmentOrderData.orderHousingData?.name;
  } else if (showRoomAssignment.value) {
    return requestedHousingName !== housingFulfillmentOrderData.roomAssignmentSelected?.[0]?.housing_name;
  }
  return true;
});

const changePriceColor = computed(() => {
  if (showCurrentRoomAssignment.value) {
    return order.value.product_details.rooms[0].total_price_in_cents !== housingFulfillmentOrderData.roomRequested?.total_price_in_cents;
  } else if (showRoomAssignment.value) {
    return housingFulfillmentOrderData.roomAssignmentSelected[0].previous_bed_price_in_cents !== housingFulfillmentOrderData.roomAssignmentSelected?.[0]?.total_price_in_cents;
  }
  return false;
});

const changeLocationColor = computed(() => {
  const requestedLocation = housingFulfillmentOrderData.roomRequested?.location;
  if (showCurrentRoomAssignment.value) {
    return requestedLocation !== housingFulfillmentOrderData?.orderHousingData?.location;
  } else if (showRoomAssignment.value) {
    return requestedLocation !== housingFulfillmentOrderData.roomAssignmentSelected?.[0]?.location;
  }
  return true;
});

const changeTierColor = computed(() => {
  const requestedTier = TIERS_OBJ[housingFulfillmentOrderData.roomRequested?.housing_data?.tier?.name];
  if (showCurrentRoomAssignment.value) {
    return requestedTier !== TIERS_OBJ[housingFulfillmentOrderData.orderHousingData?.tier?.name];
  } else if (showRoomAssignment.value) {
    return requestedTier !== TIERS_OBJ[housingFulfillmentOrderData.roomAssignmentSelected?.[0]?.housing_tier];
  }
  return true;
});

const changeSubcategoryColor = computed(() => {
  const requestedSubcategory = housingFulfillmentOrderData?.roomRequested?.housing_data?.subcategory;
  if (showCurrentRoomAssignment.value) {
    return requestedSubcategory !== housingFulfillmentOrderData?.orderHousingData?.subcategory;
  } else if (showRoomAssignment.value) {
    return requestedSubcategory !== housingFulfillmentOrderData?.roomAssignmentSelected?.[0]?.housing_subcategory;
  }
  return true;
});

const changeRoomTypeColor = computed(() => {
  const requestedType = requestedRoomType.value;
  if (showCurrentRoomAssignment.value) {
    return requestedType !== assignedRoomType.value;
  } else if (showRoomAssignment.value) {
    return requestedType !== toBeassignedRoomType.value;
  }
  return true;
});

const pricePerDayColor = computed(() => {
  const requestedPricePerDay = total_price.value.requested_price_per_day;
  if (showCurrentRoomAssignment.value) {
    return requestedPricePerDay !== total_price.value.actual_room_assigned_price_per_day;
  } else if (showRoomAssignment.value) {
    return requestedPricePerDay !== total_price.value.to_be_assigned_price_per_day;
  }
  return true;
});

const changeDurationColor = computed(() => {
  const requestedDuration = durations.value.requestedRoomDays;
  if (showCurrentRoomAssignment.value) {
    return requestedDuration !== durations.value.assignedRoomDays;
  } else if (showRoomAssignment.value) {
    return requestedDuration !== durations.value.assignedRoomDays;
  }
  return true;
});

const priceLabel = computed(() => {
  return `Price${optionSelectedId.value === HOUSING_FULFILLMENT_MODAL_OPTIONS.canceled ? ' Of Canceled Housing' : ''}:`;
});

const isCancelling = computed(() => {
  return optionSelectedId.value === HOUSING_FULFILLMENT_MODAL_OPTIONS.canceled;
});
</script>

<template>
  <div>
    <BedFulfillmentModal
      :is-modal-open="modalStates.openSelectBedsModal"
      :session="session"
      :housing="housingFulfillmentOrderData.orderHousingData"
      :order-room-types="orderRoomTypes"
      @close-modal="handleCloseBedsModal"
      @update-selected-items="updateSelectedItems"
    ></BedFulfillmentModal>

    <div v-if="modalIsOpen && !modalStates.hideModal" class="relative z-50">
      <Dialog :visible="modalIsOpen && !modalStates.hideModal" :modal="true" @update:visible="handleCloseModal" :pt="modalHeaderPassthrough" dismissableMask>
        <template #header>
          <div class="font-semibold font-montserrat text-slate-700">
            {{ participant?.fullname}}'s Housing
          </div>
        </template>
        <template #closeicon>
          <CloseIcon size="18" stroke="#037f80" stroke-width="1.75" />
        </template>
        <div
          class="mx-auto bg-white z-50 relative"
        >
          <Spinner
            v-if="isLoading"
            class="relative h-65vh bg-blue-100"
          />
          <!-- Content -->
          <div v-else="!isLoading">
            <div class="max-h-96 overflow-auto">
              <!-- Status -->
              <div v-show="housingId" class="p-4 bg-blue-425 rounded-lg">
                <div class="flex items-center w-full gap-2 flex-wrap">
                  <span class="text-sm font-semibold">Fulfillment Status</span>
                  <Dropdown
                    v-model="optionSelectedId"
                    :options="fulfillmentOptions"
                    optionLabel="label"
                    optionValue="value"
                    :disabled="state.isSavingOrder || housingFulfillmentOrderData.price_override?.disable_price_dropdown"
                    @change="e => {
                      handleRadioOption(e.value);
                      rebuildPriceOverride();
                    }"
                    placeholder="Select fulfillment status"
                  />

                  <div v-if="optionSelectedId === HOUSING_FULFILLMENT_MODAL_OPTIONS.canceled" class="basis-full flex items-center">
                    <label
                      class="mr-2 text-sm font-semibold basis-auto shrink-0 grow-1"
                      :for="`reason-for-cancellation${optionSelectedId}`"
                    >
                      Reason for Cancellation
                    </label>

                    <Dropdown
                      v-model="housingFulfillmentOrderData.cancellationReason.option"
                      class="w-full grow shrink-1 basis-0"
                      placeholder="Select cancellation reason."
                      :options="HOUSING_CANCELLATION_REASONS"
                      :inputId="`reason-for-cancellation${optionSelectedId}`"
                      optionLabel="label"
                      @change="() => (housingFulfillmentOrderData.cancellationReason.text = '') "
                    />
                  </div>

                  <div v-if="showCancellationReasonText" class="flex items-center justify-end pb-6 basis-full">
                      <SimpleTextarea
                        v-model="
                          housingFulfillmentOrderData.cancellationReason.text
                        "
                        label-name="Cancellation Reason"
                        :id="'reason-for-cancellation-explained'"
                        :vuelidate-instance="v$.cancellationReason.text"
                        :max-length="MAX_NOTE_TEXTAREA_LIMIT"
                        :disabled="state.isSavingOrder"
                        class="w-full"
                      />
                  </div>
                </div>


                <div v-if="displayBedSearchRow" class="flex flex-row flex-wrap mt-4">
                  <!--
                  Will be added post-mvp
                  <div class="basis-full">
                    <span class="text-sm font-semibold">Use Bed ID to select actual housing</span>
                  </div>
                  <div class="basis-5/6">
                    <InputGroup>
                        <InputGroupAddon>
                          <span class="material-symbols-outlined text-gray-500">search</span>
                        </InputGroupAddon>
                        <InputText disabled />
                    </InputGroup>
                  </div>-->
                  <div class="basis-full justify-items-start">
                    <div class="flex self-stretch gap-2 h-full w-full">
                      <!--Will be added post-mvp <p class="text-sm self-center">or</p>-->
                      <BaseButton
                        class="text-base font-semibold uppercase w-full align-end"
                        @click="openBedsModal"
                        :disabled="housingFulfillmentOrderData.price_override?.disable_price_dropdown"
                      >
                        Use full search
                      </BaseButton>
                    </div>
                  </div>
                </div>
              </div>


              <div v-show="housingId || fulfillingLocalHost && !housingId ">
                <div class="grid grid-cols-2">
                  <!--Requested-->
                  <div>
                      <div class="text-sm text-left font-semibold py-3 px-2 bg-gray-125 border-b-[1px] border-solid border-b-gray-200">
                        Requested Housing
                      </div>

                      <div class="flex flex-row px-2 mb-2">
                        <span class="text-sm font-semibold">
                          Housing Name:
                        </span>
                        <router-link v-if="housingFulfillmentOrderData.roomRequested?.housing_data?.id" :to="{ name: 'housing-edit', params: { itemId: housingFulfillmentOrderData.roomRequested?.housing_data?.id },}" class="ml-2 inline-flex items-start text-sm text-teal-900 hover:underline">
                          {{ housingFulfillmentOrderData.roomRequested?.housing_data?.name }}
                        </router-link>
                        <span v-else class="text-sm">
                          {{ housingFulfillmentOrderData.roomRequested?.housing_data?.name }}
                        </span>
                      </div>

                      <div class="flex flex-row px-2 gap-2 mb-2">
                        <span class="text-sm font-semibold">Subcategory: </span>
                        <span class="text-sm">
                          {{ housingFulfillmentOrderData?.roomRequested?.housing_data?.subcategory }}
                        </span>
                      </div>
                      <div class="flex flex-row px-2 gap-2">
                        <span class="text-sm font-semibold">Tier: </span>
                        <span class="text-sm">
                          {{ TIERS_OBJ[housingFulfillmentOrderData.roomRequested?.housing_data?.tier?.name] }}
                        </span>
                      </div>

                      <div class="flex flex-row items-center px-2 mb-2 gap-2">
                        <span class="text-sm font-semibold">Room Type</span>
                        <Dropdown
                          v-model="requestedRoom"
                          :options="roomTypeOptions"
                          :disabled="state.isSavingOrder || !orderRoomIsEditable || !canEditRequestedRoomType || housingFulfillmentOrderData.price_override?.disable_price_dropdown"
                        >

                          <template #value="slotProps">
                            <div class="font-medium">
                              <span>
                                {{ requestedRoomType }}
                              </span>
                            </div>
                          </template>

                          <template #option="optionSlotProps">
                              <div>
                                <div class="flex font-semibold flex-col text-md">
                                  <span>
                                    {{ optionSlotProps?.option?.housing?.name }},
                                    {{ optionSlotProps?.option?.housing?.tier?.name }}, {{ optionSlotProps?.option?.type }}
                                  </span>
                                  <span v-if="hasCityAndCountry(optionSlotProps?.option?.housing)">
                                    {{ getCityAndCountry(optionSlotProps?.option?.housing) }}
                                  </span>
                                  <em class="font-medium text-md">
                                    {{ optionSlotProps?.option?.id }}
                                  </em>
                                </div>
                              </div>
                          </template>
                        </Dropdown>
                        <ButtonWithSpinner
                          :prop-loading="state.isSavingOrderRoom"
                          class="mr-0 text-base font-semibold"
                          data-cy="save-order-room-button"
                          variant="secondary"
                          variant-type="normal"
                          :disabled="state.isSavingOrder || !orderRoomIsEditable || !canEditRequestedRoomType || !requestedRoom?.id"
                          @click.prevent="updateRoom()"
                        >
                          <span>Save</span>
                        </ButtonWithSpinner>
                      </div>

                      <div class="flex flex-row px-2 mb-2 gap-2">
                        <span class="text-sm font-semibold">Location:</span>
                        <span class="text-sm">
                          {{ housingFulfillmentOrderData.roomRequested?.location }}
                        </span>
                      </div>

                      <div class="flex flex-row px-2 mb-2 gap-2">
                        <span class="text-sm font-semibold">Duration:</span>
                        <span class="text-sm">
                          {{ durations.requestedRoomDays }} {{ durations.requestedRoomLabel }}
                        </span>
                      </div>

                      <div class="flex flex-row px-2 mb-2 gap-2 mb-2">
                        <span class="text-sm font-semibold">Price Per Day:</span>
                        <span class="text-sm">
                          {{ total_price.requested_price_per_day }}
                        </span>
                      </div>

                      <div class="flex flex-row px-2 gap-2 mb-2">
                        <span class="text-sm font-semibold">Price: </span>
                        <span class="text-sm">
                          {{ total_price.requested_room }}
                        </span>
                      </div>
                  </div>

                  <!--Assigned-->
                  <div v-show="housingId || fulfillingLocalHost && !housingId">
                      <div class="text-sm text-left font-semibold py-3 px-2 bg-gray-125 border-b-[1px] border-solid border-b-gray-200">
                        Assigned Housing
                      </div>

                      <div v-if="showRoomAssignment || showCurrentRoomAssignment" class="flex flex-row px-2 gap-2 mb-2" :class="{'text-red-400': changeHousingNameColor}">
                        <span class="text-sm font-semibold">Housing Name: </span>
                        <span v-if="showRoomAssignment" class="text-sm">
                          <router-link v-if="housingFulfillmentOrderData.roomAssignmentSelected?.[0]?.housing_id" :to="{ name: 'housing-edit', params: { itemId: housingFulfillmentOrderData.roomAssignmentSelected?.[0]?.housing_id },}" class="ml-2 inline-flex items-start text-sm text-teal-900 hover:underline">
                            {{ housingFulfillmentOrderData.roomAssignmentSelected?.[0]?.housing_name }}
                          </router-link>
                          <span v-else class="text-sm">
                            {{ housingFulfillmentOrderData.roomAssignmentSelected?.[0]?.housing_name }}
                          </span>
                        </span>
                        <span v-else-if="showCurrentRoomAssignment && housingFulfillmentOrderData.orderHousingData?.name" class="text-sm">
                          <router-link v-if="housingFulfillmentOrderData.orderHousingData?.id" :to="{ name: 'housing-edit', params: { itemId: housingFulfillmentOrderData.orderHousingData?.id },}" class="ml-2 inline-flex items-start text-sm text-teal-900 hover:underline">
                            {{ housingFulfillmentOrderData.orderHousingData.name }}
                          </router-link>
                          <span v-else class="text-sm">
                            {{ housingFulfillmentOrderData.orderHousingData.name }}
                          </span>
                        </span>
                      </div>

                      <div v-if="showRoomAssignment || showCurrentRoomAssignment" class="flex flex-row px-2 gap-2 mb-2" :class="{'text-red-400': changeSubcategoryColor}">
                        <span class="text-sm font-semibold">Subcategory: </span>
                        <span v-if="showRoomAssignment" class="text-sm">
                          {{ housingFulfillmentOrderData?.roomAssignmentSelected?.[0]?.housing_subcategory }}
                        </span>
                        <span v-else-if="showCurrentRoomAssignment && housingFulfillmentOrderData?.orderHousingData?.subcategory" class="text-sm">
                          {{ housingFulfillmentOrderData.orderHousingData.subcategory }}
                        </span>
                      </div>

                      <div v-if="showRoomAssignment || showCurrentRoomAssignment" class="flex flex-row px-2 gap-2 mb-2" :class="{'text-red-400': changeTierColor}">
                        <span class="text-sm font-semibold">Tier: </span>
                        <span v-if="showRoomAssignment" class="text-sm">
                          {{ TIERS_OBJ[housingFulfillmentOrderData.roomAssignmentSelected?.[0]?.housing_tier] }}
                        </span>
                        <span v-else-if="showCurrentRoomAssignment && housingFulfillmentOrderData?.orderHousingData?.tier?.name" class="text-sm">
                          {{ TIERS_OBJ[housingFulfillmentOrderData.orderHousingData.tier.name] }}
                        </span>
                      </div>

                      <div v-if="showRoomAssignment || showCurrentRoomAssignment" class="flex flex-row px-2 gap-2 mb-2" :class="{'text-red-400': changeRoomTypeColor}">
                        <span class="text-sm font-semibold">Room Type: </span>
                        <span v-if="showRoomAssignment" class="text-sm">
                          {{ toBeassignedRoomType }}
                        </span>
                        <span v-else-if="showCurrentRoomAssignment && housingFulfillmentOrderData?.orderHousingData?.type" class="text-sm">
                          {{ assignedRoomType }}
                        </span>
                      </div>

                      <div v-if="showRoomAssignment || showCurrentRoomAssignment" class="flex flex-row px-2 gap-2 mb-2" :class="{'text-red-400': changeLocationColor}">
                        <span class="text-sm font-semibold">Location: </span>
                        <span v-if="showRoomAssignment" class="text-sm">
                          {{ housingFulfillmentOrderData.roomAssignmentSelected?.[0]?.location }}
                        </span>
                        <span v-else-if="showCurrentRoomAssignment && housingFulfillmentOrderData?.orderHousingData?.location" class="text-sm">
                          {{ housingFulfillmentOrderData.orderHousingData.location }}
                        </span>
                      </div>

                      <div v-if="showRoomAssignment || showCurrentRoomAssignment" class="flex flex-row px-2 mb-2 gap-2 mb-2" :class="{'text-red-400': changeDurationColor}">
                        <span class="text-sm font-semibold">Duration:</span>
                        <span class="text-sm">
                          {{ durations.assignedRoomDays }} {{ durations.assignedRoomLabel }}
                        </span>
                      </div>

                      <div v-if="showRoomAssignment || showCurrentRoomAssignment" class="flex flex-row px-2 mb-2 gap-2 mb-2" :class="{'text-red-400': pricePerDayColor}">
                        <span class="text-sm font-semibold">Price Per Day:</span>
                        <span v-if="showRoomAssignment" class="text-sm">
                          {{ total_price.to_be_assigned_price_per_day }}
                        </span>
                        <span v-else-if="showCurrentRoomAssignment && housingFulfillmentOrderData.orderHousingData.price_per_day" class="text-sm">
                          {{ total_price.actual_room_assigned_price_per_day }}
                        </span>
                      </div>

                      <div v-if="showRoomAssignment || showCurrentRoomAssignment" class="flex flex-row px-2 mb-2 gap-2 mb-2">
                        <span class="text-sm font-semibold">Bed Name:</span>
                        <span v-if="showRoomAssignment" class="text-sm">
                          {{ housingFulfillmentOrderData?.roomAssignmentSelected?.[0]?.name }}
                        </span>
                        <span v-else-if="showCurrentRoomAssignment && housingFulfillmentOrderData?.orderHousingData?.bed_name" class="text-sm">
                          {{ housingFulfillmentOrderData.orderHousingData.bed_name }}
                        </span>
                      </div>

                      <!-- Price Override -->
                      <div v-if="showRoomAssignment || showCurrentRoomAssignment || fulfillingLocalHost || isCancelling">
                        <div class="flex items-center px-2 mb-2 gap-2 mb-2" :class="{'text-red-400': changePriceColor}">
                          <span class="text-sm font-semibold flex-0">
                            {{ priceLabel }}
                          </span>
                          <Dropdown
                            v-model="housingFulfillmentOrderData.price_override.price_option"
                            :disabled="housingFulfillmentOrderData.price_override?.disable_price_dropdown"
                            :options="housingFulfillmentOrderData.price_override.price_options"
                            optionLabel="label"
                            optionValue="value"
                            placeholder="Select price"
                            class="flex-1 w-full"
                            @change="() => updatePriceOverride(orderPricingOrNewRoom.lineItem)"
                          />
                        </div>

                      <div v-if="housingFulfillmentOrderData.price_override?.price_option === OPTIONS_PRICE_OVERRIDE.KEEP_ORDER.value && housingFulfillmentOrderData.price_override.current_price_config.display" class="col-start-3 col-end-5">
                        <div class="flex items-center justify-start pb-6">
                          <div class="basis-1/2 font-semibold text-right text-sm">
                            Current Product Price:
                          </div>
                          <div class="basis-1/2">
                            <span class="text-base ml-2">{{ housingFulfillmentOrderData.price_override.current_price_config.currentPriceFormatted }}</span>
                          </div>
                        </div>
                      </div>

                        <div class="flex flex-col pb-6 mt-2" v-if="housingFulfillmentOrderData.price_override?.price_option === OPTIONS_PRICE_OVERRIDE.CUSTOM_PRICE.value">
                          <div class="flex items-center justify-between w-full mb-6 gap-4">
                            <div class="text-sm font-semibold">
                              Current Product Price: <span class="font-normal">{{ total_price.requested_room }}</span>
                            </div>

                            <div class="flex items-center gap-2">
                              <label class="text-sm font-semibold">New Price $</label>
                              <InputNumber
                                v-model="housingFulfillmentOrderData.price_override.price_override.override_amount_in_dollars"
                                inputId="minmaxfraction"
                                :minFractionDigits="2"
                                :maxFractionDigits="2"
                                :min="0"
                              />
                            </div>
                          </div>


                          <div class="flex mb-6">
                            <label class="text-sm font-semibold basis-auto shrink-0 grow-1 mr-4">Override Reason</label>
                            <Dropdown
                                v-model="housingFulfillmentOrderData.price_override.price_override.override_reason_id"
                                class="w-full grow shrink-1 basis-0"
                                placeholder="Select a reason."
                                :options="customPriceReasons.housings.value"
                                inputId="custom-price-reason"
                                optionLabel="name"
                                optionValue="id"
                                :invalid="!housingFulfillmentOrderData.price_override.price_override.override_reason_id"
                              >
                                <template #option="optionSlotProps">
                                  <p>
                                    {{ optionSlotProps.option.name }}
                                  </p>
                                  <p class="ml-4 mt-2 text-gray-600 text-sm">
                                    {{ optionSlotProps.option.description }}
                                  </p>
                                </template>
                              </Dropdown>
                          </div>


                          <div class="flex">
                            <label class="text-sm font-semibold basis-auto shrink-0 grow-1 mr-4">Additional Notes</label>
                            <SimpleTextarea
                              v-model="housingFulfillmentOrderData.price_override.price_override.request_notes"
                              label-name="Additional Notes"
                              outer-classes="w-full grow shrink-1 basis-0"
                              :max-length="MAX_NOTE_TEXTAREA_LIMIT"
                              :disabled="state.isSavingOrder"
                            />
                          </div>
                        </div>

                        <div v-if="housingFulfillmentOrderData.price_override?.price_option === OPTIONS_PRICE_OVERRIDE.KEEP_ORDER.value" class="flex flex-col pb-6 mt-2">
                          <div class="flex items-center justify-end pb-6">
                            <label
                              class="mr-2 text-sm font-semibold basis-1/2 text-right"
                              :for="'keep-price-reason'"
                            >
                              Keep Order Price Reason
                            </label>
                            <Dropdown
                              v-model="housingFulfillmentOrderData.price_override.price_override.override_reason_id"
                              class="w-full basis-1/2"
                              placeholder="Select a reason."
                              :options="keepPriceReasons.housings.value"
                              inputId="keep-price-reason"
                              optionLabel="name"
                              optionValue="id"
                              :invalid="!housingFulfillmentOrderData.price_override.price_override.override_reason_id"
                            >
                              <template #option="optionSlotProps">
                                <p>
                                  {{ optionSlotProps.option.name }}
                                </p>
                                <p class="ml-4 mt-2 text-gray-600 text-sm">
                                  {{ optionSlotProps.option.description }}
                                </p>
                              </template>
                            </Dropdown>
                          </div>
                        </div>

                        <div class="col-start-3 col-end-5 flex flex pb-6 gap-2 font-montserrat" v-if="housingFulfillmentOrderData.price_override?.price_override_status?.display">
                          <p class="text-sm font-semibold">Override Request Status: </p>
                          <span class="material-symbols-outlined" :class="housingFulfillmentOrderData.price_override.price_override_status.labelColor">
                            {{ housingFulfillmentOrderData.price_override.price_override_status.icon}}
                          </span>
                          <p class="text-sm font-medium"> {{ housingFulfillmentOrderData.price_override.price_override_status.labelStatus }} </p>
                          <p>
                            <span v-if="housingFulfillmentOrderData.price_override.price_override_status.displayRevert" class="uppercase text-sm font-semibold text-teal-900 cursor-pointer" @click.prevent="cancelOrRevert(housingFulfillmentOrderData.price_override)">
                              | {{ housingFulfillmentOrderData.price_override.price_override_status.revertLabel }}
                            </span>
                          </p>
                        </div>
                      </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Actions -->
            <div class="flex justify-end mt-6 mb-6 space-x-4">
              <BaseButton
                class="text-base font-medium"
                outlined
                @click="handleCloseModal"
              >
                Cancel
              </BaseButton>
              <ButtonWithSpinner
                id="saveHousingFulfillmentOrderButton"
                :prop-loading="state.isSavingOrder"
                class="text-base font-semibold !h-7 w-24"
                data-cy="save-order-button"
                variant="secondary"
                variant-type="normal"
                @click.prevent="saveHousingFulfillmentOrder()"
              >
                <span>Save</span>
              </ButtonWithSpinner>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  </div>
</template>
